import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Plus, Trash2, X, Camera, CreditCard, Upload, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [businesses, setBusinesses] = useState([{ name: "", title: "", description: "", phone: "", email: "", website: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Photo uploads
  const [headshot, setHeadshot] = useState(null);
  const [headshotUploading, setHeadshotUploading] = useState(false);
  const [cardImages, setCardImages] = useState([]);
  const [cardUploading, setCardUploading] = useState(false);
  const headshotRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/invite/${token}`);
        setInvite(res.data);
        if (res.data.status === "approved") {
          setName(res.data.name || "");
          setEmail(res.data.email || "");
          setPhone(res.data.phone || "");
          if (res.data.business) {
            setBusinesses([{ name: res.data.business, title: "", description: "", phone: res.data.phone || "", email: res.data.email || "", website: "" }]);
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invite link");
      } finally {
        setLoading(false);
      }
    }
    load();
    document.title = "Set Up Your Profile | CHART Networking";
  }, [token]);

  function updateBusiness(index, field, value) {
    const updated = [...businesses];
    updated[index] = { ...updated[index], [field]: value };
    setBusinesses(updated);
  }

  function addBusiness() {
    setBusinesses([...businesses, { name: "", title: "", description: "", phone: "", email: "", website: "" }]);
  }

  function removeBusiness(index) {
    if (businesses.length <= 1) return;
    setBusinesses(businesses.filter((_, i) => i !== index));
  }

  async function uploadHeadshot(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeadshotUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/invite/${token}/upload-headshot`, form);
      setHeadshot(res.data.url);
    } catch { /* */ }
    finally { setHeadshotUploading(false); }
  }

  async function uploadCard(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCardUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const res = await axios.post(`${API}/invite/${token}/upload-card`, form);
        setCardImages(prev => [...prev, res.data.url]);
      }
    } catch { /* */ }
    finally { setCardUploading(false); e.target.value = ""; }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const cleanBiz = businesses.filter(b => b.name.trim());
      await axios.post(`${API}/invite/${token}/complete`, {
        name: name.trim(),
        phone,
        email,
        businessName: cleanBiz[0]?.name || "",
        title: cleanBiz[0]?.title || "",
        website: cleanBiz[0]?.website || "",
        businesses: cleanBiz.length > 0 ? cleanBiz : undefined,
      });
      setCompleted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F2EC] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E7BC4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F2EC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center border border-[#E5E0D8]">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-[#0B2A47] mb-2">Link Not Valid</h2>
          <p className="text-sm text-[#5A7080] mb-4">{error}</p>
          <Link to="/" className="inline-flex items-center px-5 py-2.5 text-sm font-bold text-white bg-[#1E7BC4] rounded-xl">
            Go to CHART Home
          </Link>
        </div>
      </div>
    );
  }

  if (invite?.status === "completed" || completed) {
    return (
      <div className="min-h-screen bg-[#F5F2EC] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center border border-[#E5E0D8]">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-[#0B2A47] mb-2">Welcome to CHART!</h2>
          <p className="text-sm text-[#5A7080] mb-4">Your profile is live. You're now on the member directory and activity tracker.</p>
          <div className="flex flex-col gap-2">
            <Link to="/members" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold text-white bg-[#1E7BC4] rounded-xl">
              View Member Directory
            </Link>
            <Link to={`/member/${encodeURIComponent(name || invite?.name)}`} className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold text-[#1E7BC4] bg-[#1E7BC4]/10 rounded-xl">
              View Your Profile
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2EC]">
      {/* Header */}
      <div className="bg-[#0B2A47] text-white">
        <div className="max-w-lg mx-auto px-5 py-8 text-center">
          <img src="/icon.png" alt="" className="h-16 mx-auto mb-3 brightness-150" />
          <h1 className="font-heading text-2xl font-extrabold tracking-tight mb-1">Welcome to CHART!</h1>
          <p className="text-[#8FC4E8] text-sm">Your application has been approved. Set up your member profile below.</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Personal Info */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-5">
            <h2 className="text-sm font-bold text-[#0B2A47] mb-4">Your Information</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Full Name *</label>
                <input value={name} onChange={e => setName(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Email</label>
                  <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Phone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" />
                </div>
              </div>
            </div>
          </div>

          {/* Headshot Upload */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-5">
            <h2 className="text-sm font-bold text-[#0B2A47] mb-1">Headshot</h2>
            <p className="text-[11px] text-[#5A7080] mb-4">Optional — add a photo so members can put a face to the name.</p>
            <input ref={headshotRef} type="file" accept="image/*" onChange={uploadHeadshot} className="hidden" />
            {headshot ? (
              <div className="flex items-center gap-4">
                <img src={headshot} alt="Headshot" className="w-20 h-20 rounded-full object-cover border-2 border-[#1E7BC4]" />
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Uploaded</p>
                  <button type="button" onClick={() => headshotRef.current?.click()} className="text-[11px] font-bold text-[#1E7BC4] hover:underline">
                    Change Photo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => headshotRef.current?.click()}
                disabled={headshotUploading}
                className="w-full py-4 border-2 border-dashed border-[#D5D0C8] hover:border-[#1E7BC4] rounded-xl flex flex-col items-center gap-2 transition-colors"
              >
                {headshotUploading ? (
                  <Loader2 className="w-6 h-6 text-[#1E7BC4] animate-spin" />
                ) : (
                  <Camera className="w-6 h-6 text-[#5A7080]" />
                )}
                <span className="text-xs font-semibold text-[#5A7080]">
                  {headshotUploading ? "Uploading..." : "Tap to upload a headshot"}
                </span>
              </button>
            )}
          </div>

          {/* Businesses */}
          {businesses.map((biz, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E5E0D8] p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-[#0B2A47]">
                  {businesses.length > 1 ? `Business ${i + 1}` : "Your Business"}
                </h2>
                {businesses.length > 1 && (
                  <button type="button" onClick={() => removeBusiness(i)} className="p-1 text-red-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Business Name</label>
                  <input value={biz.name} onChange={e => updateBusiness(i, "name", e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="Your company" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Your Title / Role</label>
                  <input value={biz.title} onChange={e => updateBusiness(i, "title", e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="e.g. Owner, Agent, Consultant" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">What You Do</label>
                  <textarea value={biz.description} onChange={e => updateBusiness(i, "description", e.target.value)} rows={2} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47] resize-none" placeholder="Brief description of your services" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Business Phone</label>
                    <input value={biz.phone} onChange={e => updateBusiness(i, "phone", e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Business Email</label>
                    <input value={biz.email} onChange={e => updateBusiness(i, "email", e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Website</label>
                  <input value={biz.website} onChange={e => updateBusiness(i, "website", e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="yoursite.com" />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addBusiness}
            className="w-full py-2.5 text-xs font-bold text-[#1E7BC4] bg-[#1E7BC4]/5 hover:bg-[#1E7BC4]/10 border border-dashed border-[#1E7BC4]/30 rounded-xl transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add Another Business
          </button>

          {/* Business Card Photos */}
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-5">
            <h2 className="text-sm font-bold text-[#0B2A47] mb-1">Business Card Photos</h2>
            <p className="text-[11px] text-[#5A7080] mb-4">Upload photos of your business cards. Select front and back at the same time or add them one by one.</p>
            <input ref={cardRef} type="file" accept="image/*" multiple onChange={uploadCard} className="hidden" />

            {cardImages.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {cardImages.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt={`Card ${i + 1}`} className="w-full rounded-lg border border-[#E5E0D8] object-cover aspect-[1.6/1]" />
                    <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-black/60 text-white rounded">
                      {i === 0 ? "Front" : i === 1 ? "Back" : `Card ${i + 1}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => cardRef.current?.click()}
              disabled={cardUploading}
              className="w-full py-4 border-2 border-dashed border-[#D5D0C8] hover:border-[#1E7BC4] rounded-xl flex flex-col items-center gap-2 transition-colors"
            >
              {cardUploading ? (
                <Loader2 className="w-6 h-6 text-[#1E7BC4] animate-spin" />
              ) : (
                <CreditCard className="w-6 h-6 text-[#5A7080]" />
              )}
              <span className="text-xs font-semibold text-[#5A7080]">
                {cardUploading ? "Uploading..." : cardImages.length > 0 ? "Add more card photos" : "Tap to upload business card photos (front & back)"}
              </span>
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting || !name.trim()}
            className="w-full py-3.5 text-sm font-bold text-white bg-[#0B2A47] hover:bg-[#0D3358] rounded-xl disabled:opacity-40 transition-colors"
          >
            {submitting ? "Setting Up..." : "Complete My Profile"}
          </button>
        </form>
      </div>
    </div>
  );
}
